import { Check, X } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import ColorPicker from "components/ColorPicker";
import RuvixxDate from "components/RuvixxDate";
import tagConstants from "constants/Tag";
import {
  TAG_COLUMN_IDS,
  TAG_COLUMN_KEYS,
  TAG_FILTER_KEYS,
  TAG_FILTER_TITLES,
} from "routes/tags/TagManagementTableV3";
import { TagTableActions } from "routes/tags/components/TagTableActions";

export const getTagManagementTableColumns = ({
  canEditTag = false,
  canDeleteTag = false,
  onEditTag = () => {},
  onCloneTag = () => {},
  onDeleteTag = () => {},
}) => {
  const columns = [
    {
      id: TAG_COLUMN_IDS.SELECT,
      size: 60,
      enableSorting: false,
      meta: {
        isDisplay: true,
      },
      header: ({ table }) => (
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onCheckedChange: checked => table.toggleAllRowsSelected(!!checked),
            "aria-label": "Select all rows",
          }}
        />
      ),
      cell: ({ row }) => (
        <div>
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected() && !row.getIsSelected(),
              onCheckedChange: checked => row.toggleSelected(!!checked),
              "aria-label": "Select row",
            }}
          />
        </div>
      ),
    },
    {
      header: TAG_COLUMN_IDS.ID,
      id: TAG_COLUMN_IDS.ID,
      accessorKey: TAG_COLUMN_KEYS.ID,
      size: 50,
      cell: ({ row }) => (
        <p style={{ fontWeight: "bold" }}>{row.getValue(TAG_COLUMN_IDS.ID)}</p>
      ),
    },
    {
      header: TAG_COLUMN_IDS.NAME,
      id: TAG_COLUMN_IDS.NAME,
      accessorKey: TAG_COLUMN_KEYS.NAME,
      cell: ({ row }) => <div>{row.getValue(TAG_COLUMN_IDS.NAME)}</div>,
    },
    {
      header: TAG_COLUMN_IDS.DESCRIPTION,
      id: TAG_COLUMN_IDS.DESCRIPTION,
      accessorKey: TAG_COLUMN_KEYS.DESCRIPTION,
      cell: ({ row }) => <div>{row.getValue(TAG_COLUMN_IDS.DESCRIPTION)}</div>,
    },
    {
      header: TAG_COLUMN_IDS.COLOR,
      id: TAG_COLUMN_IDS.COLOR,
      accessorKey: TAG_COLUMN_KEYS.COLOR,
      size: 80,
      enableSorting: false,
      cell: ({ row }) => (
        <ColorPicker color={row.getValue(TAG_COLUMN_IDS.COLOR)} small />
      ),
    },
    {
      header: TAG_COLUMN_IDS.TAGGABLE_MODELS,
      id: TAG_COLUMN_IDS.TAGGABLE_MODELS,
      accessorKey: TAG_COLUMN_KEYS.TAGGABLE_MODELS,
      cell: ({ row }) => {
        const models = row.getValue(TAG_COLUMN_IDS.TAGGABLE_MODELS) || [];
        return (
          <ScrollColumn>
            {models
              .filter(key => !!tagConstants.taggableModels[key])
              .map(key => (
                <li key={key}>{tagConstants.taggableModels[key]}</li>
              ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: "View Only",
      id: TAG_COLUMN_IDS.READ_ROLES,
      accessorKey: TAG_COLUMN_KEYS.READ_ROLES,
      enableSorting: false,
      cell: ({ row }) => {
        const roles = row.getValue(TAG_COLUMN_IDS.READ_ROLES) || [];
        if (!roles.length) return "All Roles";
        return (
          <ScrollColumn>
            {roles.map(role => (
              <li key={role.id}>{role.name}</li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: "View and Edit",
      id: TAG_COLUMN_IDS.EDIT_ROLES,
      accessorKey: TAG_COLUMN_KEYS.EDIT_ROLES,
      enableSorting: false,
      cell: ({ row }) => {
        const roles = row.getValue(TAG_COLUMN_IDS.EDIT_ROLES) || [];
        if (!roles.length) return "All Roles";
        return (
          <ScrollColumn>
            {roles.map(role => (
              <li key={role.id}>{role.name}</li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: TAG_COLUMN_IDS.ENABLED,
      id: TAG_COLUMN_IDS.ENABLED,
      accessorKey: TAG_COLUMN_KEYS.ENABLED,
      cell: ({ row }) => {
        const enabled = row.getValue(TAG_COLUMN_IDS.ENABLED);
        return enabled ? (
          <Check className="tw:size-4 tw:text-[#24B388]" aria-label="Enabled" />
        ) : (
          <X
            className="tw:size-4 tw:text-muted-foreground"
            aria-label="Disabled"
          />
        );
      },
    },
    {
      header: TAG_COLUMN_IDS.CATEGORY,
      id: TAG_COLUMN_IDS.CATEGORY,
      accessorKey: TAG_COLUMN_KEYS.CATEGORY,
      cell: ({ row }) => {
        const category = row.original.tag_category;
        return <div>{category?.name || null}</div>;
      },
    },
    {
      header: TAG_COLUMN_IDS.CREATED_BY,
      id: TAG_COLUMN_IDS.CREATED_BY,
      accessorKey: TAG_COLUMN_KEYS.CREATED_BY,
      cell: ({ row }) => {
        const user = row.original.created_by_user;
        return <div>{user?.full_name || null}</div>;
      },
    },
    {
      header: TAG_COLUMN_IDS.CREATED_AT,
      id: TAG_COLUMN_IDS.CREATED_AT,
      accessorKey: TAG_COLUMN_KEYS.CREATED_AT,
      cell: ({ row }) => (
        <RuvixxDate date={row.getValue(TAG_COLUMN_IDS.CREATED_AT)} />
      ),
    },
    {
      header: TAG_COLUMN_IDS.UPDATED_BY,
      id: TAG_COLUMN_IDS.UPDATED_BY,
      accessorKey: TAG_COLUMN_KEYS.UPDATED_BY,
      cell: ({ row }) => {
        const user = row.original.updated_by_user;
        return <div>{user?.full_name || null}</div>;
      },
    },
    {
      header: TAG_COLUMN_IDS.UPDATED_AT,
      id: TAG_COLUMN_IDS.UPDATED_AT,
      accessorKey: TAG_COLUMN_KEYS.UPDATED_AT,
      cell: ({ row }) => (
        <RuvixxDate date={row.getValue(TAG_COLUMN_IDS.UPDATED_AT)} />
      ),
    },
    {
      header: null,
      id: TAG_COLUMN_IDS.ACTIONS,
      accessorKey: TAG_COLUMN_KEYS.ACTIONS,
      size: 60,
      enableSorting: false,
      enableResizing: false,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <TagTableActions
          tag={row.original}
          canEditTag={canEditTag}
          canDeleteTag={canDeleteTag}
          onEditTag={onEditTag}
          onCloneTag={onCloneTag}
          onDeleteTag={onDeleteTag}
        />
      ),
    },
  ];

  return columns;
};

export const getTagManagementTableFilters = ({
  tagCategories = [],
  taggableModels = [],
}) => {
  return [
    {
      key: TAG_FILTER_KEYS.TAGGABLE_MODELS,
      title: TAG_FILTER_TITLES.TAGGABLE_MODELS,
      type: "select",
      data: taggableModels,
    },
    {
      key: TAG_FILTER_KEYS.CATEGORY_IDS,
      title: TAG_FILTER_TITLES.CATEGORY,
      type: "select",
      data: tagCategories,
    },
  ];
};
